import { Link } from 'react-router-dom';
import { fmtHM, GUARANTEE, tierRange, fmtPrice, TIER_KEYS, PAX_KEYS } from '../data/layoverConfig';
import { localizePath } from '../i18nRoutes';
import { trackWidgetEvent } from '../lib/widgetAnalytics';

export const LAYOVER_HUB = '/layover-tours-istanbul';
export const LAYOVER_CHILDREN = [
  { slug: '7-hours', label: '7-hour layover', labelAr: 'توقف 7 ساعات' },
  { slug: '8-hours', label: '8-hour layover', labelAr: 'توقف 8 ساعات' },
  { slug: '10-hours-plus', label: '10+ hour layover', labelAr: 'توقف 10 ساعات أو أكثر' },
  { slug: 'istanbul-airport-ist', label: 'From Istanbul Airport (IST)', labelAr: 'من مطار إسطنبول (IST)' },
  { slug: 'sabiha-gokcen-saw', label: 'From Sabiha Gökçen (SAW)', labelAr: 'من مطار صبيحة كوكجن (SAW)' },
];

export const hubPath = (lang) => localizePath(LAYOVER_HUB, lang);
export const childPath = (slug, lang) => localizePath(`${LAYOVER_HUB}/${slug}`, lang);
export const hubUrl = (lang) => `https://viaturkey.net${hubPath(lang)}`;
export const childUrl = (slug, lang) => `https://viaturkey.net${childPath(slug, lang)}`;

const MONTHS_AR = { January: 'يناير', February: 'فبراير', March: 'مارس', April: 'أبريل', May: 'مايو', June: 'يونيو', July: 'يوليو', August: 'أغسطس', September: 'سبتمبر', October: 'أكتوبر', November: 'نوفمبر', December: 'ديسمبر' };
const dateAr = (d) => String(d || '').replace(/[A-Za-z]+/g, (m) => MONTHS_AR[m] || m);

const T = {
  en: {
    guarantee: 'Our guarantee:', navLabel: 'Layover pages', overview: 'Layover tours overview',
    indicative: 'Indicative price', layoverCol: 'Layover', p13: '1–3 people', p46: '4–6 people', p7: (max) => `7–${max} people`,
    rows: { '7h': '7 hours', '8h': '8 hours', '10h': '10+ hours' },
    free: 'Free', day: 'day', evening: 'evening',
    tierIntro: 'Per group, not per person — the price depends on your layover length and group size.',
    includes: 'Includes a licensed guide and a private vehicle for both airport transfers. Excludes entrance fees and food — food breaks are at your own cost.',
    label: 'Indicative — confirmed when we plan your itinerary.', fees: (d) => `Current entrance fees (last checked ${d})`, ta: 'Book on Tripadvisor',
    meetH: 'Where we meet and where we return you', dontH: "Don't book this if…", touristH: "Turkish Airlines' free Touristanbul tour — how is this different?",
    touristP: "Touristanbul is Turkish Airlines' free group tour for passengers connecting through Istanbul Airport on a single Turkish Airlines ticket. It is a good programme. Ours is different in five ways: it is private to your party; you choose the route; there is no fixed departure slot to match; your guide is licensed and speaks your language; and if your inbound flight is late we re-plan around the hours you still have instead of leaving without you. If you qualify for Touristanbul and its slot matches your flights, take it — we will say so.",
    sawTip: 'Kadıköy market is quieter on Sunday mornings, and the ferries thin out in winter — we plan the shoreline walk around the timetable on the day.',
    istTipBefore: 'If your connection is at Sabiha Gökçen, read the ', istTipLink: 'SAW page', istTipAfter: ' — the routing is different and Kadıköy market is quieter on Sunday mornings; ferries thin out in winter.',
    visaH: 'Visa', visaBefore: 'Requirements depend on your nationality — check the official e-Visa site at ', visaAfter: '. We do not give immigration advice.',
    note: 'Estimates only. Traffic and queues vary.',
    compliance: 'Viaturkey is a consultancy: nothing on this page confirms availability or a final price, and we do not take bookings or payments online. Tour bookings are facilitated through TÜRSAB-licensed partner agencies after we have planned your itinerary with you.',
    touristType: ['Transit passengers', 'Layover travellers'],
    offerDesc: (cfg) => `Indicative, per group of up to ${cfg.group_max}. Excludes entrance fees and food. Back at the airport ${fmtHM(cfg.return_buffer_min)} before departure.`,
  },
  ar: {
    guarantee: 'ضماننا:', navLabel: 'صفحات جولات الترانزيت', overview: 'نظرة عامة على جولات الترانزيت',
    indicative: 'السعر التقريبي', layoverCol: 'مدة التوقف', p13: '1–3 أشخاص', p46: '4–6 أشخاص', p7: (max) => `7–${max} أشخاص`,
    rows: { '7h': '7 ساعات', '8h': '8 ساعات', '10h': '10 ساعات أو أكثر' },
    free: 'مجاناً', day: 'نهاراً', evening: 'مساءً',
    tierIntro: 'السعر لكل مجموعة لا لكل شخص، ويعتمد على مدة التوقف وعدد المسافرين.',
    includes: 'يشمل مرشداً مرخّصاً وسيارة خاصة للنقل من المطار وإليه. ولا يشمل رسوم الدخول والطعام — استراحات الطعام على نفقتك الخاصة.',
    label: 'سعر تقريبي — يُؤكَّد عند تخطيط برنامجك.', fees: (d) => `رسوم الدخول الحالية (آخر تحقق: ${dateAr(d)})`, ta: 'احجز عبر Tripadvisor',
    meetH: 'أين نلتقيك وأين نعيدك', dontH: 'لا تحجز هذه الجولة إذا…', touristH: 'جولة Touristanbul المجانية من الخطوط الجوية التركية — ما الفرق؟',
    touristP: 'Touristanbul جولة جماعية مجانية تقدمها الخطوط الجوية التركية للمسافرين العابرين عبر مطار إسطنبول بتذكرة واحدة على الخطوط التركية. إنها برنامج جيد. أما جولتنا فتختلف في خمسة أمور: هي خاصة بمجموعتك وحدها؛ وأنت من يختار المسار؛ ولا يوجد موعد انطلاق ثابت عليك الالتزام به؛ ومرشدك مرخّص ويتحدث لغتك؛ وإن تأخرت رحلتك القادمة نعيد التخطيط وفق الساعات المتبقية لك بدلاً من الانطلاق من دونك. وإن كنت مؤهلاً لجولة Touristanbul وكان موعدها يناسب رحلاتك، فاخترها — وسنقول لك ذلك.',
    sawTip: 'يكون سوق قاضي كوي أهدأ صباح يوم الأحد، وتقلّ رحلات العبّارات في الشتاء — نخطط لجولة الواجهة البحرية وفق الجدول في اليوم نفسه.',
    istTipBefore: 'إن كان توقفك في مطار صبيحة كوكجن، فاقرأ ', istTipLink: 'صفحة SAW', istTipAfter: ' — فالمسار مختلف، وسوق قاضي كوي أهدأ صباح يوم الأحد، وتقلّ رحلات العبّارات في الشتاء.',
    visaH: 'التأشيرة', visaBefore: 'تختلف المتطلبات بحسب جنسيتك — تحقّق من موقع التأشيرة الإلكترونية الرسمي ', visaAfter: '. نحن لا نقدّم استشارات في شؤون الهجرة والتأشيرات.',
    note: 'تقديرات فقط. تختلف حركة المرور والطوابير.',
    compliance: 'فياتركي شركة استشارات: لا شيء في هذه الصفحة يؤكد التوفر أو السعر النهائي، ولا نستقبل حجوزات أو مدفوعات عبر الإنترنت. تُنظَّم حجوزات الجولات عبر وكالات شريكة مرخّصة من TÜRSAB بعد أن نخطط برنامجك معك.',
    touristType: ['المسافرون العابرون', 'مسافرو الترانزيت'],
    offerDesc: (cfg) => `سعر تقريبي لكل مجموعة حتى ${cfg.group_max} أشخاص. لا يشمل رسوم الدخول والطعام. العودة إلى المطار قبل المغادرة بـ${fmtHM(cfg.return_buffer_min, 'ar')}.`,
  },
};
const tx = (lang) => T[lang] || T.en;

export const feeLabel = (f, lang = 'en') => {
  const t = tx(lang);
  if (f.kind === 'free') return t.free;
  if (f.kind === 'day_evening') return `${f.amount} (${t.day}) / ${f.amount_evening} (${t.evening})`;
  return f.amount;
};

export function LayoverGuarantee({ cfg, lang = 'en' }) {
  return <div className="lo-guarantee" data-testid="layover-guarantee"><i className="fas fa-shield-alt"></i> <strong>{tx(lang).guarantee}</strong> {GUARANTEE(cfg, lang)}</div>;
}

export function LayoverSiblingNav({ current, lang = 'en' }) {
  const t = tx(lang);
  return (
    <nav className="lo-siblings" aria-label={t.navLabel} data-testid="layover-sibling-nav">
      <Link to={hubPath(lang)} className={!current ? 'active' : ''}>{t.overview}</Link>
      {LAYOVER_CHILDREN.map(c => <Link key={c.slug} to={childPath(c.slug, lang)} className={current === c.slug ? 'active' : ''}>{lang === 'ar' ? c.labelAr : c.label}</Link>)}
    </nav>
  );
}

export function LayoverPriceBlock({ cfg, durationKey, lang = 'en' }) {
  const t = tx(lang);
  const rows = durationKey ? [durationKey] : TIER_KEYS;
  return (
    <div className="lo-price" data-testid="layover-price-block">
      <h3>{t.indicative}</h3>
      <p data-testid="layover-tier-intro">{t.tierIntro}</p>
      <table className="gp-table lo-tier-table" data-testid="layover-tier-table">
        <thead><tr><th>{t.layoverCol}</th><th>{t.p13}</th><th>{t.p46}</th><th>{t.p7(cfg.group_max)}</th></tr></thead>
        <tbody>
          {rows.map(k => (
            <tr key={k} data-testid={`layover-tier-row-${k}`}><td>{t.rows[k]}</td>{PAX_KEYS.map(p => <td key={p}>{fmtPrice(cfg, cfg.tiers?.[k]?.[p])}</td>)}</tr>
          ))}
        </tbody>
      </table>
      <p>{t.includes}</p>
      <p className="lo-label"><em>{t.label}</em></p>
      {cfg.entrance_fees?.length > 0 && (
        <details className="lo-fees" data-testid="layover-entrance-fees">
          <summary>{t.fees(cfg.entrance_fees_checked)}</summary>
          <ul>{cfg.entrance_fees.map((f, i) => <li key={i} data-testid={`layover-fee-${i}`}><strong>{lang === 'ar' && f.site_ar ? f.site_ar : f.site}:</strong> {feeLabel(f, lang)}</li>)}</ul>
        </details>
      )}
      {cfg.ta_url_layover && (
        <a href={cfg.ta_url_layover} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" onClick={() => trackWidgetEvent('layover_cta_clicked', { widget: 'layover', channel: 'tripadvisor' })} data-testid="layover-tripadvisor-btn"><i className="fab fa-tripadvisor"></i> {t.ta}</a>
      )}
    </div>
  );
}

export function LayoverCommonBlocks({ cfg, meet, dontBook, airport = 'IST', lang = 'en' }) {
  const t = tx(lang);
  return (
    <>
      <h2>{t.meetH}</h2>
      <p>{meet}</p>
      <h2>{t.dontH}</h2>
      <ul className="lo-dontbook" data-testid="layover-dont-book">{dontBook.map((d, i) => <li key={i}>{d}</li>)}</ul>
      <h2>{t.touristH}</h2>
      <p>{t.touristP}</p>
      {airport === 'SAW' && <p className="lo-tip" data-testid="layover-kadikoy-tip">{t.sawTip}</p>}
      {airport !== 'SAW' && <p className="lo-tip">{t.istTipBefore}<Link to={childPath('sabiha-gokcen-saw', lang)}>{t.istTipLink}</Link>{t.istTipAfter}</p>}
      <h2>{t.visaH}</h2>
      <p>{t.visaBefore}<a href="https://www.evisa.gov.tr" target="_blank" rel="noopener noreferrer">evisa.gov.tr</a>{t.visaAfter}</p>
      <p className="lo-note" data-testid="layover-estimates-note"><em>{t.note}</em></p>
      <p className="lo-compliance" data-testid="layover-compliance">{t.compliance}</p>
    </>
  );
}

export function buildLayoverSchema({ cfg, url, name, description, itinerary, faqs, breadcrumbs, airport = 'IST', durationKey, lang = 'en' }) {
  const t = tx(lang);
  const range = tierRange(cfg, durationKey || null);
  const offers = range
    ? { '@type': 'AggregateOffer', priceCurrency: cfg.tier_currency, lowPrice: String(range.low), highPrice: String(range.high), offerCount: String(durationKey ? PAX_KEYS.length : TIER_KEYS.length * PAX_KEYS.length), availability: 'https://schema.org/InStock', description: t.offerDesc(cfg) }
    : undefined;
  return [
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: breadcrumbs.map((b, i) => ({ '@type': 'ListItem', position: i + 1, name: b.name, item: b.url })) },
    { '@context': 'https://schema.org', '@type': 'TouristTrip', name, description, url, inLanguage: lang, touristType: t.touristType,
      itinerary: { '@type': 'ItemList', itemListElement: itinerary.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it })) },
      provider: { '@type': 'TravelAgency', '@id': 'https://viaturkey.net/#travel-agency', name: 'Viaturkey Investments & Tourism' },
      ...(offers ? { offers } : {}) },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];
}
